// Utilitaire gratuit pour l'optimisation SEO automatique
export interface SEOSuggestions {
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  canonicalUrl: string;
}

export const generateSEOSuggestions = (
  title: string,
  content: string,
  category: string
): SEOSuggestions => {
  // Nettoyer le contenu HTML
  const cleanContent = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  
  // Extraire les mots-clés du contenu
  const keywords = extractKeywords(cleanContent, title);
  
  // Générer le meta title optimisé
  const metaTitle = generateMetaTitle(title, category);
  
  // Générer la meta description
  const metaDescription = generateMetaDescription(cleanContent, keywords);
  
  // Générer l'URL canonique
  const canonicalUrl = generateCanonicalUrl(title);
  
  return {
    metaTitle,
    metaDescription,
    keywords: keywords.join(', '),
    canonicalUrl
  };
};

const extractKeywords = (content: string, title: string): string[] => {
  // Mots-clés de base pour le business
  const baseKeywords = [
    'château gonflable',
    'location château',
    'anniversaire enfant',
    'fête enfant',
    'Rhode-Saint-Genèse',
    'Belgique',
    'sumo'
  ];
  
  // Extraire les mots importants du titre et du contenu
  const words = [...title.toLowerCase().split(/\s+/), ...content.toLowerCase().split(/\s+/)]
    .filter(word => word.length > 3)
    .filter(word => !isStopWord(word));
  
  // Compter la fréquence des mots
  const wordCount = words.reduce((acc, word) => {
    acc[word] = (acc[word] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  // Prendre les mots les plus fréquents
  const topWords = Object.entries(wordCount)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .map(([word]) => word);
  
  return [...baseKeywords, ...topWords].slice(0, 10);
};

const isStopWord = (word: string): boolean => {
  const stopWords = [
    'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'et', 'à', 'avec',
    'pour', 'dans', 'sur', 'par', 'ce', 'qui', 'que', 'est', 'sont',
    'avoir', 'être', 'faire', 'dire', 'aller', 'voir', 'savoir', 'prendre',
    'venir', 'falloir', 'devoir', 'vouloir', 'pouvoir', 'très', 'plus',
    'tout', 'bien', 'encore', 'aussi', 'comme', 'après', 'avant'
  ];
  return stopWords.includes(word.toLowerCase());
};

const generateMetaTitle = (title: string, category: string): string => {
  // Limiter à 60 caractères max
  const brandSuffix = ' | Château de Clémentine';
  const maxLength = 60 - brandSuffix.length;
  
  let optimizedTitle = title;
  
  // Ajouter la catégorie si elle n'est pas déjà dans le titre
  if (!title.toLowerCase().includes(category.toLowerCase()) && category) {
    optimizedTitle = `${category}: ${title}`;
  }
  
  // Tronquer si nécessaire
  if (optimizedTitle.length > maxLength) {
    optimizedTitle = optimizedTitle.substring(0, maxLength - 3) + '...';
  }
  
  return optimizedTitle + brandSuffix;
};

const generateMetaDescription = (content: string, keywords: string[]): string => {
  // Prendre les premiers 120 mots du contenu
  const words = content.split(/\s+/).slice(0, 25);
  let description = words.join(' ');
  
  // Ajouter un appel à l'action
  const cta = ' Réservez dès maintenant pour votre fête!';
  const maxLength = 160 - cta.length;
  
  if (description.length > maxLength) {
    description = description.substring(0, maxLength - 3) + '...';
  }
  
  return description + cta;
};

const generateCanonicalUrl = (title: string): string => {
  const slug = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
    .replace(/[^a-z0-9\s-]/g, '') // Garder seulement lettres, chiffres, espaces et tirets
    .replace(/\s+/g, '-') // Remplacer espaces par tirets
    .replace(/-+/g, '-') // Supprimer tirets multiples
    .replace(/^-|-$/g, ''); // Supprimer tirets en début/fin
  
  return `https://chateaudeclementine.com/blog/${slug}`;
};

// Fonction pour estimer le temps de lecture
export const calculateReadTime = (content: string): number => {
  const wordsPerMinute = 200;
  const words = content.replace(/<[^>]*>/g, ' ').split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
};